// Subscription and Billing Service
// Aligned with actual DIDI backend responses from /api/subscriptions/*
import { httpClient } from './http.client.js';
import { API_BASE_URL } from './api.constants.js';

const API_URL = API_BASE_URL;

// =============================================================================
// Types — match actual backend response structures
// =============================================================================

/** GET /api/subscriptions/usage → { success, data } */
export interface UsageStats {
  credits: {
    remaining: number;
    spent: number;
    total: number;
    usagePercent: number;
  };
  plan: {
    id: number;
    name: string;
    /** Backend plan_type (1=Freemium..6=Enterprise, 7=PAYG). Pro tier = type >= 4. */
    type?: number;
    creditsPerCycle: number;
    maxImages: number;
    maxVideoMinutes: number;
    storageLimitGb: number;
    priceAmount: number;
  };
  subscription: {
    isActive: boolean;
    activationDate: string | null;
    deactivationDate: string | null;
  };
}

/** GET /api/subscriptions/plans → { success, data: Plan[] } */
export interface Plan {
  id: number;
  name: string;
  type?: number;
  billingPeriod?: number;
  price?: number;
  priceAmount: number;
  priceUsd?: number;
  creditsPerCycle: number;
  maxImages?: number;
  maxVideoMinutes?: number;
  storageLimitGb?: number;
}

/** Credit costs per analysis type */
export const ANALYSIS_COSTS: Record<string, number> = {
  text: 1,
  url: 1,
  image: 2,
  audio: 3,
  video: 5,
};

/** Wrapper for backend { success, data } pattern */
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// =============================================================================
// Service
// =============================================================================

export class SubscriptionService {
  /**
   * Get usage stats (credits, plan, subscription status)
   */
  static async getUsageStats(): Promise<UsageStats> {
    const res = await httpClient.get<ApiResponse<UsageStats>>(`${API_URL}/api/subscriptions/usage`);
    return res.data;
  }

  /**
   * Get all available plans
   */
  static async getPlans(): Promise<Plan[]> {
    const res = await httpClient.get<ApiResponse<any[]>>(`${API_URL}/api/subscriptions/plans`);
    return (res.data || []).map((p) => ({
      ...p,
      priceAmount: p.priceAmount ?? p.priceUsd ?? p.price ?? 0,
    }));
  }

  /**
   * Get color based on usage percentage
   */
  static getUsageColor(percentage: number): string {
    if (percentage >= 90) return '#f44336';
    if (percentage >= 75) return '#ff9800';
    return '#4caf50';
  }

  /**
   * Check if user can afford an analysis
   */
  static canAfford(remaining: number, analysisType: string): boolean {
    const cost = ANALYSIS_COSTS[analysisType] || 1;
    return remaining >= cost;
  }

  /**
   * Start Stripe Checkout for upgrading to a paid plan.
   * Returns the redirect URL — caller does `window.location.href = url`.
   */
  static async createCheckoutSession(planId: number, interval: 'month' | 'year' = 'month'): Promise<{ url: string; sessionId: string }> {
    const res = await httpClient.post<ApiResponse<{ url: string; sessionId: string }>>(
      `${API_URL}/api/subscriptions/upgrade`,
      { plan_id: planId, interval }
    );
    return res.data;
  }

  /**
   * Open Stripe Customer Portal for self-service (cancel, update card, view invoices).
   */
  static async createPortalSession(): Promise<{ url: string }> {
    const res = await httpClient.post<ApiResponse<{ url: string }>>(
      `${API_URL}/api/subscriptions/portal`,
      {}
    );
    return res.data;
  }
}
